import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import { toast } from 'sonner'
import { Button3D } from '@/components/learner/ui/Button3D'
import { Card } from '@/components/learner/ui/Card'
import { LearnerLoading } from '@/components/learner/ui/LearnerLoading'
import { Mascot } from '@/components/learner/ui/Mascot'
import { CourseAccordion } from '@/components/learner/ui/CourseAccordion'
import { FilterChipRow } from '@/components/learner/ui/FilterChipRow'
import { curriculumAccentBgClass } from '@/components/learner/ui/courseTheme'
import { useLearnerLibrary } from '@/hooks/learner/useLearnerLibrary'
import { cn } from '@/lib/utils'
import { getLearnerErrorMessage } from '@/utils/errorMessage'
import { findCurrentCourseId, toLibraryData } from './model/libraryModel'
import type { Course, LibraryData, Lesson } from './ui-model/types'

type FilterId = 'all' | 'completed'

function filterCourse(course: Course, filter: FilterId, query: string): Course {
  const normalizedQuery = query.trim().toLocaleLowerCase()
  return {
    ...course,
    modules: course.modules
      .map((module) => ({
        ...module,
        lessons: module.lessons.filter((lesson) => {
          const matchesStatus = filter === 'all' || lesson.status === 'done'
          const matchesQuery =
            normalizedQuery.length === 0 ||
            lesson.title.toLocaleLowerCase().includes(normalizedQuery)
          return matchesStatus && matchesQuery
        }),
      }))
      .filter((module) => module.lessons.length > 0),
  }
}

export default function Library() {
  const { t } = useTranslation(['learner', 'common'])
  const [filter, setFilter] = useState<FilterId>('all')
  const [query, setQuery] = useState('')
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null)
  const currentLessonRef = useRef<HTMLButtonElement>(null)
  const hasAttemptedInitialScrollRef = useRef(false)
  const navigate = useNavigate()
  const libraryQuery = useLearnerLibrary()
  const data = useMemo<LibraryData | null>(
    () => libraryQuery.data ? toLibraryData(libraryQuery.data) : null,
    [libraryQuery.data],
  )

  const defaultCourseId = useMemo(
    () => findCurrentCourseId(data?.courses ?? []),
    [data?.courses],
  )
  const activeCourse = useMemo(
    () =>
      data?.courses.find((course) => course.id === selectedCourseId) ??
      data?.courses.find((course) => course.id === defaultCourseId) ??
      null,
    [data?.courses, defaultCourseId, selectedCourseId],
  )
  const visibleCourse = useMemo(
    () => (activeCourse ? filterCourse(activeCourse, filter, query) : null),
    [activeCourse, filter, query],
  )
  const currentLessonId = useMemo(() => {
    for (const course of data?.courses ?? []) {
      for (const module of course.modules) {
        const currentLesson = module.lessons.find((lesson) => lesson.status === 'current')
        if (currentLesson) return currentLesson.id
      }
    }
    return null
  }, [data?.courses])

  useEffect(() => {
    if (!data || hasAttemptedInitialScrollRef.current) return

    hasAttemptedInitialScrollRef.current = true
    if (currentLessonId == null) return

    currentLessonRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    })
  }, [currentLessonId, data])

  const handleLessonClick = useCallback(
    (lesson: Lesson) => {
      if (lesson.status === 'locked') {
        toast.info(t('library.lockedToast'))
        return
      }
      if (lesson.status === 'done') {
        navigate(`/my/library/${lesson.id}`)
        return
      }
      navigate(`/my/learn/${lesson.id}`)
    },
    [navigate, t],
  )

  const totalLessons = activeCourse?.totalLessons ?? 0
  const completedLessons = activeCourse?.completedLessons ?? 0

  const filterChips = useMemo(
    () => [
      { id: 'all' as const, label: t('library.filter.all'), count: totalLessons },
      { id: 'completed' as const, label: t('library.filter.completed'), count: completedLessons },
    ],
    [completedLessons, t, totalLessons],
  )

  if (libraryQuery.isLoading) {
    return <LearnerLoading />
  }

  if (libraryQuery.isError) {
    return (
      <Frame>
        <Card
          size="md"
          role="alert"
          aria-live="assertive"
          className="flex flex-col items-center gap-4 py-8 text-center"
        >
          <p className="text-lc-body-2 text-lc-gray-700">
            {getLearnerErrorMessage(libraryQuery.error, t)}
          </p>
          <Button3D variant="lightblue" size="M" onClick={() => void libraryQuery.refetch()}>
            {t('common:retry')}
          </Button3D>
        </Card>
      </Frame>
    )
  }

  if (!data || data.courses.length === 0) {
    return (
      <Frame>
        <div className="flex flex-col items-center justify-center gap-5 py-20">
          <Mascot name="chaeli" shape="roundbox" size={120} decorative />
          <h1 className="text-lc-headline-3 text-lc-gray-900">{t('library.noHistory.title')}</h1>
          <p className="text-lc-body-2 text-lc-gray-700">{t('library.noHistory.desc')}</p>
        </div>
      </Frame>
    )
  }

  return (
    <Frame labelledBy="library-heading">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col items-start gap-1.5">
          <h1 id="library-heading" className="text-lc-v3-h3 font-bold text-lc-v3-gray-950">
            {t('library.title')}
          </h1>
          <p className="text-lc-v3-body-2 font-medium text-lc-v3-gray-600">
            {t('library.subtitle')}
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <FilterChipRow
            chips={filterChips}
            selectedId={filter}
            onSelect={(id) => setFilter(id as FilterId)}
            scrollable
          />
          <label className="inline-flex h-9 w-full items-center gap-2 rounded-full border border-lc-v3-gray-300 bg-lc-bg-primary px-4 sm:w-60">
            <Search className="size-4 shrink-0 text-lc-text-tertiary" aria-hidden />
            <input
              placeholder={t('library.search.placeholder')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label={t('library.search.placeholder')}
              className="min-w-0 flex-1 bg-transparent text-lc-v3-body-2 font-medium text-lc-text-primary outline-none placeholder:text-lc-text-tertiary"
            />
          </label>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div
          role="tablist"
          aria-label={t('library.courseTabs.label')}
          className="-mx-4 flex gap-2 overflow-x-auto px-4 md:mx-0 md:px-0"
        >
          {data.courses.map((course) => {
            const selected = course.id === activeCourse?.id
            return (
              <button
                key={course.id}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls="library-course-panel"
                onClick={() => setSelectedCourseId(course.id)}
                className={cn(
                  'h-12 min-w-36 flex-1 shrink-0 rounded-xl border px-4 text-lc-v3-body-1 font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500',
                  selected
                    ? `${curriculumAccentBgClass[course.accent]} border-transparent text-lc-text-inverse`
                    : 'border-lc-v3-gray-300 bg-lc-bg-primary text-lc-text-primary hover:bg-lc-v3-gray-50',
                )}
              >
                <ContentMetadataText text={course.title} />
              </button>
            )
          })}
        </div>

        {visibleCourse ? (
          <div id="library-course-panel" role="tabpanel">
            <CourseAccordion
              key={visibleCourse.id}
              course={visibleCourse}
              defaultOpen
              emptyText={
                filter === 'completed'
                  ? t('library.empty.completedOnly')
                  : t('library.empty.filtered')
              }
              onLessonClick={handleLessonClick}
              currentLessonId={currentLessonId}
              currentLessonRef={currentLessonRef}
            />
          </div>
        ) : null}
      </div>
    </Frame>
  )
}

function Frame({
  children,
  labelledBy,
}: {
  children: React.ReactNode
  labelledBy?: string
}) {
  return (
    <section
      aria-labelledby={labelledBy}
      className="mx-auto flex w-full max-w-[1040px] flex-col gap-10 px-4 pb-10 pt-6 animate-lc-rise md:px-10 md:pt-10"
    >
      {children}
    </section>
  )
}
